import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useSmartBack } from "@/lib/navigation-utils";
import { Button } from "@akuna/ui";
import { Input } from "@akuna/ui";
import { Label } from "@/components/ui/label";
import { ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function ForgotPassword() {
    const { t } = useTranslation("auth");
    const { t: tCommon } = useTranslation("common");
    const [email, setEmail] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const { resetPassword } = useAuth();
    const smartBack = useSmartBack();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);

        const { error } = await resetPassword(email);

        if (!error) {
            setIsSuccess(true);
        }

        setIsLoading(false);
    };

    if (isSuccess) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-primary/5 p-4">
                <div className="w-full max-w-md">
                    <div className="text-center mb-8">
                        <Link to="/" className="inline-block mb-6">
                            <h1 className="text-3xl font-bold text-primary">Akuna</h1>
                        </Link>
                        <h2 className="text-2xl font-bold text-foreground mb-2">{t("forgotPasswordSentTitle")}</h2>
                        <p className="text-muted-foreground">
                            {t("forgotPasswordSentSubtitle")}
                        </p>
                    </div>

                    <div className="bg-card rounded-lg shadow-lg p-8 border border-border">
                        <div className="text-center space-y-4">
                            <p className="text-sm text-muted-foreground">
                                {t("forgotPasswordRetryHint")}
                            </p>

                            <Button
                                variant="outline"
                                className="w-full"
                                onClick={() => {
                                    setIsSuccess(false);
                                    setEmail("");
                                }}
                            >
                                {t("forgotPasswordTryAnother")}
                            </Button>

                            <Button
                                variant="ghost"
                                className="w-full"
                                onClick={() => smartBack("/login")}
                            >
                                <ArrowLeft className="mr-2 h-4 w-4" />
                                {t("backToLogin")}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-primary/5 p-4">
            <div className="w-full max-w-md">
                <div className="text-center mb-8">
                    <Link to="/" className="inline-block mb-6">
                        <h1 className="text-3xl font-bold text-primary">Akuna</h1>
                    </Link>
                    <h2 className="text-2xl font-bold text-foreground mb-2">{t("forgotPasswordTitle")}</h2>
                    <p className="text-muted-foreground">
                        {t("forgotPasswordSubtitle")}
                    </p>
                </div>

                <div className="bg-card rounded-lg shadow-lg p-8 border border-border">
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="email">{tCommon("email")}</Label>
                            <Input
                                id="email"
                                type="email"
                                placeholder={t("emailPlaceholder")}
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                disabled={isLoading}
                            />
                        </div>

                        <Button type="submit" className="w-full" disabled={isLoading}>
                            {isLoading ? t("forgotPasswordSending") : t("forgotPasswordSubmit")}
                        </Button>

                        <Button
                            type="button"
                            variant="ghost"
                            className="w-full"
                            onClick={() => smartBack("/login")}
                            disabled={isLoading}
                        >
                            <ArrowLeft className="mr-2 h-4 w-4" />
                            {t("backToLogin")}
                        </Button>
                    </form>
                </div>
            </div>
        </div>
    );
}
